import {uniqBy} from 'lodash';
import moment from 'moment';
import React, {useEffect, useMemo, useState} from 'react';
import DatePicker from 'react-datepicker';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Icon,
  Modal,
} from 'semantic-ui-react';
import {DropdownItemProps} from 'semantic-ui-react';

interface AppointmentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  userOptions: DropdownItemProps[];
  userSearchOptions?: DropdownItemProps[];
  onSearchUsers: (options: any[], searchObj: any) => void;
  initialData?: any;
  isEdit: boolean;
  loggedInCompany: any;
}

const AppointmentModal: React.FunctionComponent<AppointmentModalProps> = (
  props: AppointmentModalProps,
) => {
  const [user, setUser] = useState<string>('');
  const [description, setDescription] = useState('');
  const [multiDay, setMultiDay] = useState(true);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  const mergedUserOptions = useMemo(() => {
    const base = (props.userOptions || []).map((opt: any) => ({
      key: opt.key || opt.value,
      value: opt.value,
      text: opt.text,
    }));
    const search = (props.userSearchOptions || []).map((opt: any) => ({
      key: opt.key || opt.value,
      value: opt.value,
      text: opt.text,
    }));
    return uniqBy([...base, ...search], 'value');
  }, [props.userOptions, props.userSearchOptions]);

  useEffect(() => {
    if (props.open && props.initialData) {
      const job = props.initialData;
      const stage = job.stages?.[job.stage];

      setUser(stage?.assignedTo?.[0] || '');
      setDescription(job.description || '');
      setMultiDay(!!stage?.dueAllDay);

      if (stage?.start) {
        setStartDate(moment(stage.start).toDate());
        if (!stage.dueAllDay) {
          setStartTime(moment(stage.start).toDate());
        }
      }
      if (stage?.due) {
        setEndDate(moment(stage.due).toDate());
        if (!stage.dueAllDay) {
          setEndTime(moment(stage.due).toDate());
        }
      }
    } else if (props.open) {
      setUser('');
      setDescription('');
      setMultiDay(true);
      setStartDate(null);
      setEndDate(null);
      setStartTime(null);
      setEndTime(null);
    }
  }, [props.open, props.initialData]);

  const isValid = () => {
    if (!user || !description.trim()) return false;
    if (multiDay) {
      return !!startDate && !!endDate;
    }
    return !!startDate && !!startTime && !!endTime;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let apiStartDate: string;
      let apiEndDate: string;

      if (multiDay) {
        apiStartDate = moment(startDate).format('YYYY-MM-DD');
        apiEndDate = moment(endDate).format('YYYY-MM-DD');
      } else {
        const date = moment(startDate).format('YYYY-MM-DD');
        apiStartDate = `${date}T${moment(startTime).format('HH:mm')}`;
        apiEndDate = `${date}T${moment(endTime).format('HH:mm')}`;
      }

      await props.onSave({
        _id: props.initialData?._id,
        company: props.loggedInCompany._id,
        user,
        description: description.trim(),
        startDate: apiStartDate,
        endDate: apiEndDate,
        startAllDay: multiDay,
        dueAllDay: multiDay,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!props.onDelete || !props.initialData?._id) return;
    setSaving(true);
    try {
      await props.onDelete(props.initialData._id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      size="small"
      className="AppointmentModalContainer"
      dimmer="inverted"
    >
      <Modal.Header>
        {props.isEdit ? 'Edit Appointment' : 'Add Appointment'}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field required>
            <label>Assign To</label>
            <Dropdown
              placeholder="Type to search Users"
              noResultsMessage="Type to start searching."
              fluid
              selection
              search
              icon="search"
              selectOnBlur={false}
              value={user}
              options={mergedUserOptions}
              onSearchChange={(options, searchObj) =>
                props.onSearchUsers(options as any, searchObj)
              }
              onChange={(_, data) => setUser(data.value as string)}
            />
          </Form.Field>

          <Form.TextArea
            label="Description"
            required
            placeholder="e.g. Holiday, Doctor's appointment..."
            value={description}
            onChange={(_, data) => setDescription(data.value as string)}
          />

          <Form.Field>
            <Checkbox
              toggle
              label="All Day"
              checked={multiDay}
              onChange={(_, data) => {
                setMultiDay(!!data.checked);
                setEndDate(null);
                setStartTime(null);
                setEndTime(null);
              }}
            />
          </Form.Field>

          {multiDay ? (
            <>
              <Form.Field required>
                <label>Start Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date) => setStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select start date"
                  isClearable
                />
              </Form.Field>
              <Form.Field required>
                <label>End Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date) => setEndDate(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select end date"
                  minDate={startDate || undefined}
                  isClearable
                />
              </Form.Field>
            </>
          ) : (
            <>
              <Form.Field required>
                <label>Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date) => setStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                  isClearable
                />
              </Form.Field>
              <Form.Group widths="equal">
                <Form.Field required>
                  <label>Start Time</label>
                  <DatePicker
                    selected={startTime}
                    onChange={(date: Date) => setStartTime(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Start"
                    dateFormat="HH:mm"
                    placeholderText="HH:mm"
                  />
                </Form.Field>
                <Form.Field required>
                  <label>End Time</label>
                  <DatePicker
                    selected={endTime}
                    onChange={(date: Date) => setEndTime(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="End"
                    dateFormat="HH:mm"
                    placeholderText="HH:mm"
                    minTime={startTime || undefined}
                    maxTime={moment().endOf('day').toDate()}
                  />
                </Form.Field>
              </Form.Group>
            </>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        {props.isEdit && props.onDelete && (
          <Button
            className="ui redButton"
            icon
            labelPosition="left"
            floated="left"
            loading={saving}
            onClick={handleDelete}
          >
            <Icon name="delete" />
            Delete
          </Button>
        )}
        <Button
          className="ui secondary"
          icon
          labelPosition="left"
          onClick={props.onClose}
        >
          <Icon name="delete" />
          Cancel
        </Button>
        <Button
          className="ui primary"
          icon
          labelPosition="left"
          loading={saving}
          disabled={!isValid() || saving}
          onClick={handleSave}
        >
          <Icon name="check" />
          {props.isEdit ? 'Save' : 'Add'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AppointmentModal;
